import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import { Star, Printer, Music, MailCheck, MoonStar } from "lucide-react";
import { fetchPackArt, resolveImage } from "@/lib/api";
import { PACK_DAYS, BEDTIME_PRAYER } from "./packContent";

const printArt = (day) => {
  const el = document.getElementById(`pack-art-${day.day}`);
  if (!el) return;
  const markup =
    el.tagName.toLowerCase() === "img"
      ? el.outerHTML.replace("<img", '<img style="max-width:700px;width:100%"')
      : el.outerHTML.replace("<svg", '<svg width="700" height="700"');
  const w = window.open("", "_blank");
  if (!w) return;
  w.document.write(
    `<html><head><title>${day.title} — colouring sheet</title></head>` +
    `<body style="margin:0;display:grid;place-items:center;min-height:100vh">` +
    `<div style="text-align:center;font-family:Arial,sans-serif">` +
    `<h2 style="margin:0 0 4px">${day.title}</h2>` +
    `<p style="margin:0 0 16px;color:#64748B">${day.verse} — Little Songs. Big Faith.</p>` +
    `${markup}</div></body></html>`
  );
  w.document.close();
  w.focus();
  setTimeout(() => w.print(), 300);
};

const PackView = () => {
  const location = useLocation();
  const name = location.state?.name;
  const [art, setArt] = useState({});

  useEffect(() => {
    fetchPackArt().then(setArt).catch(() => {});
  }, []);

  return (
    <main className="min-h-screen bg-[#FFFDF6] pb-24" data-testid="pack-view-page">
      <header className="mx-auto flex max-w-6xl items-center justify-between px-4 pt-6 sm:px-6">
        <Link to="/" className="flex items-center gap-2" data-testid="pack-view-logo">
          <span className="grid h-9 w-9 place-items-center rounded-full bg-[#FFC107] shadow-inner">
            <Star className="h-5 w-5 fill-white text-white" />
          </span>
          <span className="font-display text-base font-semibold tracking-tight sm:text-lg">
            Little Songs. <span className="text-[#0284C7]">Big Faith.</span>
          </span>
        </Link>
        <Link
          to="/sing"
          data-testid="pack-view-sing-link"
          className="rounded-full bg-slate-900 px-4 py-2 text-sm font-bold text-white transition-transform hover:scale-[1.04]"
        >
          Sing the songs
        </Link>
      </header>

      <div className="mx-auto max-w-4xl px-4 sm:px-6">
        {name && (
          <motion.div
            initial={{ opacity: 0, y: -12 }}
            animate={{ opacity: 1, y: 0 }}
            className="mt-8 flex items-start gap-3 rounded-3xl border border-[#BBF7D0] bg-[#F0FDF4] p-5"
            data-testid="pack-welcome-banner"
          >
            <MailCheck className="mt-0.5 h-6 w-6 shrink-0 text-[#16A34A]" />
            <div>
              <p className="font-display text-lg font-bold text-slate-900">
                You&apos;re in, {name}! Your pack is unlocked below.
              </p>
              <p className="mt-1 text-sm text-slate-600">
                A copy is on its way to your inbox (peek in spam if it hides). Over the next week
                we&apos;ll send our best-loved songs and more printable ideas — unsubscribe anytime.
              </p>
            </div>
          </motion.div>
        )}

        <div className="mt-12 text-center">
          <span className="inline-flex items-center gap-2 rounded-full border border-[#FCD34D] bg-white px-4 py-2 text-xs font-bold uppercase tracking-widest text-slate-600">
            <Star className="h-3.5 w-3.5 fill-[#FFC107] text-[#FFC107]" />
            Your 7-Day Little Faith Activity Pack
          </span>
          <h1 className="mt-5 font-display text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl">
            One little faith moment, every day for a week.
          </h1>
          <p className="mx-auto mt-3 max-w-xl text-base text-slate-600">
            Print the colouring page, do the simple activity, sing the song, say the verse.
            Ten minutes a day — that&apos;s the whole thing.
          </p>
        </div>

        <div className="mt-12 grid gap-6">
          {PACK_DAYS.map((d, i) => (
            <motion.section
              key={d.day}
              initial={{ opacity: 0, y: 28 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.5, delay: (i % 3) * 0.08 }}
              className="overflow-hidden rounded-3xl border border-[#E2DDD0] bg-white shadow-[0_10px_30px_-5px_rgba(234,179,8,0.1)]"
              data-testid={`pack-day-${d.day}`}
            >
              <div className="grid sm:grid-cols-[240px_1fr]">
                <div className="grid place-items-center border-b border-[#F1EDE0] bg-[#F8F5EC] p-6 sm:border-b-0 sm:border-r">
                  {art[String(d.day)] ? (
                    <img
                      id={`pack-art-${d.day}`}
                      src={resolveImage(art[String(d.day)])}
                      alt={`${d.title} colouring sheet`}
                      className="h-44 w-44 rounded-2xl object-contain"
                    />
                  ) : (
                    <d.Art id={`pack-art-${d.day}`} className="h-44 w-44" />
                  )}
                  <button
                    onClick={() => printArt(d)}
                    data-testid={`pack-print-${d.day}`}
                    className="mt-4 inline-flex items-center gap-2 rounded-full border-2 border-slate-900 bg-white px-5 py-2.5 text-sm font-bold text-slate-900 transition-all hover:bg-slate-900 hover:text-white"
                  >
                    <Printer className="h-4 w-4" /> Print colouring sheet
                  </button>
                </div>
                <div className="p-6 sm:p-8">
                  <div className="flex items-center gap-3">
                    <span className="rounded-full px-3 py-1 text-xs font-bold text-white" style={{ backgroundColor: d.color }}>
                      Day {d.day}
                    </span>
                    <span className="text-xs font-bold uppercase tracking-widest text-slate-400">{d.verse}</span>
                  </div>
                  <h2 className="mt-3 font-display text-2xl font-bold text-slate-900">{d.title}</h2>
                  <p className="mt-2 font-hand text-xl leading-snug text-slate-500">
                    &ldquo;{d.verseText}&rdquo;
                  </p>
                  <p className="mt-3 text-sm leading-relaxed text-slate-600 sm:text-base">{d.activity}</p>
                  <div className="mt-5 flex flex-wrap items-center gap-3">
                    <Link
                      to="/sing"
                      data-testid={`pack-song-${d.day}`}
                      className="inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-bold text-white transition-transform hover:scale-[1.04]"
                      style={{ backgroundColor: d.color }}
                    >
                      <Music className="h-4 w-4" /> Sing: {d.song.title}
                    </Link>
                  </div>
                  {d.prayer && (
                    <div className="mt-6 rounded-2xl bg-[#F8F5EC] p-5" data-testid="pack-bedtime-prayer">
                      <p className="flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-[#9333EA]">
                        <MoonStar className="h-4 w-4" /> The bedtime prayer
                      </p>
                      <p className="mt-3 font-hand text-2xl leading-relaxed text-slate-700">
                        &ldquo;{BEDTIME_PRAYER}&rdquo;
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </motion.section>
          ))}
        </div>

        <div className="mt-12 rounded-3xl bg-slate-900 p-8 text-center" data-testid="pack-membership-cta">
          <h2 className="font-display text-2xl font-bold text-white">
            Loved the pack? There&apos;s a whole library waiting.
          </h2>
          <p className="mx-auto mt-2 max-w-lg text-sm leading-relaxed text-slate-300">
            The full song library, gentle bedtime playlists, Bible story adventures and a kind,
            parent-first community — free to start, always.
          </p>
          <Link
            to="/signin"
            state={{ mode: "signup" }}
            data-testid="pack-join-btn"
            className="mt-6 inline-flex items-center gap-2 rounded-full bg-[#FFC107] px-8 py-4 font-display text-base font-semibold text-slate-900 transition-transform hover:scale-[1.04]"
          >
            Join Little Songs. Big Faith. free
          </Link>
        </div>
      </div>
    </main>
  );
};

export default PackView;
